import commonApi from 'apis/commonApi';

let captchaLoading: Promise<void> | null = null;
let captchaConfig: Promise<{ enabled: boolean; siteKey: string }> | null = null;

function extractCaptchaEnabled(data: any): boolean {
  if (!data) return false;

  const sources = [data, data.data, data.result];
  for (const source of sources) {
    if (!source || source.RegisterWithCapcha === undefined || source.RegisterWithCapcha === null) continue;

    const flag = source.RegisterWithCapcha;
    if (typeof flag === 'number') return flag === 1;
    if (typeof flag === 'boolean') return flag;
    if (typeof flag === 'string') return flag.trim() === '1';
  }

  return false;
}

async function getCaptchaConfig(): Promise<{ enabled: boolean; siteKey: string }> {
  if (captchaConfig) return captchaConfig;

  captchaConfig = commonApi.getVersionApp({}).then(({ data }) => {
    const enabled = extractCaptchaEnabled(data);
    const siteKey: string | undefined = data.recapchaToken;

    if (enabled && !siteKey) {
      throw new Error('Missing reCAPTCHA site key');
    }

    return { enabled, siteKey: siteKey || '' };
  }).catch((err) => {
    captchaConfig = null;
    throw err;
  });

  return captchaConfig;
}

async function loadCaptchaScript(siteKey: string): Promise<void> {
  if (captchaLoading) {
    return captchaLoading;
  }

  captchaLoading = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onerror = reject;
    script.onload = () => {
      window.grecaptcha.ready(resolve);
    };
    document.head.appendChild(script);
  }).catch((err) => {
    captchaLoading = null;
    throw err;
  });

  return captchaLoading;
}

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export async function getRecaptchaToken(action = 'register'): Promise<string | null> {
  try {
    const { enabled, siteKey } = await getCaptchaConfig();
    if (!enabled || !siteKey) {
      return null;
    }

    await loadCaptchaScript(siteKey);
    return window.grecaptcha.execute(siteKey, { action });
  } catch {
    return null;
  }
}
